import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useProgression } from '../../auth/ProgressionProvider';
import { useSeason } from '../../auth/SeasonProvider';
import { listBuiltinPianoTracks } from '@shared/builtinPianoMaps';
import {
  chartDisplayBpm,
  chartDurationSeconds,
  formatDuration,
  stripVariantSuffix,
} from '@shared/chart';
import { VARIANTS, type Variant, variantLabel } from '@shared/gameTypes';
import { saveModePref } from '../../game/modePref';
import { modePath } from '../../game/modeRouting';
import { isTutorialUnseen } from '../../game/tutorialFlag';
import { TrophyStrip } from '../../components/TrophyStrip';
import { ModeSeoSection } from '../ModeSeoSection';
import '../../components/trophyStrip.css';
import '../home.css';
import './pianoHome.css';

const VARIANT_STORAGE_KEY = 'gravitydash:piano-home-variant:v1';

function loadStoredVariant(): Variant {
  try {
    const raw = localStorage.getItem(VARIANT_STORAGE_KEY);
    if (raw && (VARIANTS as ReadonlyArray<string>).includes(raw)) {
      return raw as Variant;
    }
  } catch (err) {
    console.warn('Failed to read variant from localStorage:', err);
  }
  return 'normal';
}

function persistVariant(variant: Variant): void {
  try {
    localStorage.setItem(VARIANT_STORAGE_KEY, variant);
  } catch (err) {
    console.warn('Failed to persist variant to localStorage:', err);
  }
}

const VARIANT_DESCRIPTIONS: Record<Variant, string> = {
  normal: 'Steady tempo, clear runs. Build your muscle memory.',
  hard: 'Denser tile streams, occasional dual accents, tighter timing.',
  extreme: 'Maximum density and the tightest hit windows. Late-game speed-up still climbs from cup 3.',
};

/**
 * Piano-mode home page. Mirrors the Pulse and Circles homes — track
 * grid keyed by `(track, variant)` from the builtin Piano catalogue,
 * variant tabs with their own storage key so cross-mode picks don't
 * clobber each other.
 */
export function PianoHomePage() {
  const { forMap } = useProgression();
  const { crownsForMap } = useSeason();
  const tracks = listBuiltinPianoTracks();
  const navigate = useNavigate();
  const [variant, setVariant] = useState<Variant>(() => loadStoredVariant());

  useEffect(() => {
    saveModePref('piano');
  }, []);

  useEffect(() => {
    // Per-mode tutorial gate: a brand-new Piano player gets diverted
    // to /piano/tutorial on first arrival, mirroring the Circles home
    // flow. AuthGate guarantees auth has resolved before this mounts,
    // so the localStorage check is safe to run unconditionally.
    if (isTutorialUnseen('piano')) {
      navigate(modePath('piano', 'tutorial'), { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== VARIANT_STORAGE_KEY && event.key !== null) {
        return;
      }
      setVariant(loadStoredVariant());
    };
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const handleVariantChange = (next: Variant) => {
    setVariant(next);
    persistVariant(next);
  };

  const featuredTrack = tracks[0];
  const featuredChart = featuredTrack.variants[variant].chart;

  return (
    <div className="home piano-home">
      <header className="home-hero">
        <h1 className="home-hero-kicker">
          BEAT<span style={{ color: 'var(--accent-magenta)' }}>PIANO</span>
        </h1>
        <div className="home-cta">
          <Link
            to={modePath('piano', `play/${featuredChart.slug}`)}
            className="btn btn-primary btn-large"
            data-testid="piano-play-cta"
          >
            Play {stripVariantSuffix(featuredChart)}
          </Link>
          <Link
            to={modePath('piano', 'tutorial')}
            className="btn"
            data-testid="piano-tutorial-cta"
          >
            How to play
          </Link>
          <Link to="/calibrate" className="btn btn-ghost">
            Calibrate audio
          </Link>
        </div>
      </header>

      <section className="home-maps">
        <div className="home-maps-header">
          <h2>Tracks</h2>
          <p className="muted faint">
            All 12 NCS songs, three difficulty variants each.
          </p>
        </div>

        <div
          className="variant-selector"
          role="group"
          aria-label="Piano density"
          data-testid="piano-variant-selector"
        >
          {VARIANTS.map((v) => (
            <button
              key={v}
              type="button"
              aria-pressed={variant === v}
              className={`variant-tab variant-tab-${v} ${variant === v ? 'is-active' : ''}`}
              onClick={() => handleVariantChange(v)}
              data-testid={`piano-variant-tab-${v}`}
            >
              {variantLabel(v)}
            </button>
          ))}
          <p className="variant-hint muted" aria-live="polite">
            {VARIANT_DESCRIPTIONS[variant]}
          </p>
        </div>

        <ol className="map-list">
          {tracks.map((track, idx) => {
            const chart = track.variants[variant].chart;
            const tileCount = chart.notes.length;
            const trophies = forMap(chart.slug, 'piano');
            return (
              <li key={track.baseSlug} className="map-list-item">
                <Link
                  to={modePath('piano', `play/${chart.slug}`)}
                  className="map-row piano-map-row"
                  data-testid={`piano-map-card-${track.baseSlug}`}
                  style={
                    {
                      '--map-bg': chart.palette.bg,
                      '--map-bg-accent': chart.palette.bgAccent,
                      '--map-pad': chart.palette.pad,
                      '--map-player': chart.palette.player,
                    } as React.CSSProperties
                  }
                >
                  <div className="map-row-index">{String(idx + 1).padStart(2, '0')}</div>
                  <div className="map-row-preview piano-map-row-preview">
                    {[0, 1, 2, 3].map((laneIdx) => (
                      <div
                        key={laneIdx}
                        className="piano-preview-lane"
                        style={{ '--lane-index': laneIdx } as React.CSSProperties}
                      />
                    ))}
                  </div>
                  <div className="map-row-body">
                    <div className="map-row-title">{stripVariantSuffix(chart)}</div>
                    <div className="map-row-desc">{chart.description}</div>
                  </div>
                  <div className="map-row-meta">
                    <div className="map-row-bpm">{chartDisplayBpm(chart.bpm).toFixed(0)} BPM</div>
                    <div className="map-row-duration" title="Duration">
                      {formatDuration(chartDurationSeconds(chart.bpm, chart.totalBeats))}
                    </div>
                    <div
                      className="map-row-pad-count"
                      title={`${tileCount} tiles on ${variantLabel(variant)}`}
                    >
                      {tileCount} tiles
                    </div>
                    <div
                      className="map-row-difficulty"
                      aria-label={`Difficulty ${chart.difficulty}`}
                    >
                      {Array.from({ length: 5 }, (_, i) => (
                        <span
                          key={i}
                          className={i < chart.difficulty ? 'pip pip-on' : 'pip'}
                        />
                      ))}
                    </div>
                    <TrophyStrip
                      stars={trophies.stars}
                      cups={trophies.cups}
                      crowns={crownsForMap(chart.slug, 'piano')}
                      size="sm"
                    />
                  </div>
                </Link>
              </li>
            );
          })}
        </ol>
      </section>

      <ModeSeoSection
        mode="piano"
        playHref={modePath('piano', `play/${featuredChart.slug}`)}
        playLabel={`Play ${stripVariantSuffix(featuredChart)}`}
      />
    </div>
  );
}
